<template>
  <b-modal
    ref="modal"
    size="lg"
    title="Settings"
    centered
    dialog-class="settings-modal-dialog"
    cancel-variant="light"
    @hide="closeHandler"
    @ok="saveCompressSettings"
    @show="fetchCompressSettings"
  >
    <template #modal-ok>
      Save
    </template>

    <b-tabs>
      <b-tab
        title="General"
      >
        <div
          v-if="isCompressSettingsFetching"
          class="justify-content-center align-items-center loading settings-loading"
        >
          <div
            class="spinner-border text-primary"
            role="status"
          >
            <span class="sr-only">Loading...</span>
          </div>
        </div>

        <div class="form-group mt-3">
          <label class="general-settings-label">
            Grid View Tile Size
            <a
              href="#"
              class="info-tooltip video-compress-quality-tooltip"
              data-toggle="tooltip"
              title="Controls thumbnail size in grid view."
            >
              <InfoIcon
                :width="10"
                :height="10"
              />
            </a>
          </label>
          <div class="d-flex align-items-center gap-2">
            <Grid3x3Icon
              :width="24"
              :height="24"
            />
            <input
              class="form-control-range flex-grow-1 tile-size-slider"
              type="range"
              min="0"
              max="100"
              step="1"
              :value="thumbSize"
              :style="{ '--tile-size-pct': `${thumbSize}%` }"
              @input="onThumbSizeInput"
            >
            <LayoutGridIcon
              :width="24"
              :height="24"
            />
          </div>
        </div>

        <div class="form-group mt-3">
          <label class="general-settings-label">
            Video Compress Quality
            <a
              href="#"
              class="info-tooltip video-compress-quality-tooltip"
              data-toggle="tooltip"
              title="Note that lower values correspond to higher bitrates, and hence produce higher quality videos."
            >
              <InfoIcon
                :width="10"
                :height="10"
              />
            </a>
          </label>
          <select
            class="form-control"
            :value="compressSettingsData[SETTINGS_MODAL_FIELDS_NAME.quality_compress_video]"
            @change="filedHandler(SETTINGS_MODAL_FIELDS_NAME.quality_compress_video, $event.target.value)"
          >
            <option
              v-for="quality in compressQuality"
              :key="quality"
              :value="quality"
            >
              {{ quality }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label class="general-settings-label">
            Image Compress Max Size
            <a
              href="#"
              class="info-tooltip image-compress-max-size-tooltip"
              data-toggle="tooltip"
              title="Max resolution value for the image. If the original image resolution is higher than this value, the image will be resized with this value."
            >
              <InfoIcon
                :width="10"
                :height="10"
              />
            </a>
          </label>
          <input
            type="text"
            class="form-control"
            :value="compressSettingsData[SETTINGS_MODAL_FIELDS_NAME.image_compress_max_size_webp]"
            @input="filedHandler(SETTINGS_MODAL_FIELDS_NAME.image_compress_max_size_webp, $event.target.value)"
          >
        </div>

        <div class="form-group">
          <label class="general-settings-label">
            Image Compress Quality
            <a
              href="#"
              class="info-tooltip image-compress-quality-tooltip"
              data-toggle="tooltip"
              title="Image compression level. Quality is 1 (lowest image quality and highest compression) to 100 (best quality but least effective compression). The default is to use the estimated quality of your input image if it can be determined, otherwise 92."
            >
              <InfoIcon
                :width="10"
                :height="10"
              />
            </a>
          </label>
          <input
            type="text"
            class="form-control"
            :value="compressSettingsData[SETTINGS_MODAL_FIELDS_NAME.image_compress_quality]"
            @input="filedHandler(SETTINGS_MODAL_FIELDS_NAME.image_compress_quality, $event.target.value)"
          >
        </div>

        <div class="form-group">
          <label class="general-settings-label">
            Video Convert Setting
          </label>
          <div class="form-group">
            <b-form-checkbox
              value="on"
              unchecked-value="off"
              :checked="compressSettingsData[SETTINGS_MODAL_FIELDS_NAME.when_convert_delete_original_file]"
              @change="filedHandler(SETTINGS_MODAL_FIELDS_NAME.when_convert_delete_original_file, $event)"
            >
              <span class="general-settings-label">When converting, delete original file</span>
            </b-form-checkbox>
          </div>
        </div>
      </b-tab>

      <b-tab title="Advanced">
        <div
          class="c-settings-advanced d-flex flex-column mt-3"
        >
          <p class="general-settings-label mb-2">
            Maintenance actions. Each runs once and notifies you on completion.
          </p>

          <b-button
            :disabled="isReindexing"
            variant="light"
            class="mt-2"
            @click="reindexHandler"
          >
            <b-spinner
              v-if="isReindexing"
              small
              label="Spinning"
            />
            <RefreshCwIcon
              :width="16"
              :height="16"
              class="mb-1"
            />
            <span class="general-settings-label">
              Reindex Files
            </span>
          </b-button>

          <b-button
            :disabled="isGenerating"
            variant="light"
            class="mt-2"
            @click="generateThumbnailHandler"
          >
            <b-spinner
              v-if="isGenerating"
              small
              label="Spinning"
            />
            <ImageIcon
              :width="16"
              :height="16"
              class="mb-1"
            />
            <span class="general-settings-label">
              Generate Thumbnails
            </span>
          </b-button>

          <b-button
            :disabled="isRegenerating"
            variant="light"
            class="mt-2"
            @click="reGenerateThumbnailHandler"
          >
            <b-spinner
              v-if="isRegenerating"
              small
              label="Spinning"
            />
            <RotateCcwIcon
              :width="16"
              :height="16"
              class="mb-1"
            />
            <span class="general-settings-label">
              Re-generate Thumbnails
            </span>
          </b-button>
        </div>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import {MUTATIONS} from "../../../../../store/file-browser";
import {SETTINGS_MODAL_FIELDS_NAME} from "./fields";
import mixin from "../mixin";
import {
  RefreshCw as RefreshCwIcon,
  Image as ImageIcon,
  RotateCcw as RotateCcwIcon,
  Info as InfoIcon,
  Grid3x3 as Grid3x3Icon,
  LayoutGrid as LayoutGridIcon
} from "lucide-vue";
import {clampGridDensity} from "../../grid-density";

const { mapActions, mapGetters, mapMutations } = createNamespacedHelpers('fileBrowser')

export default {
  name: "SettingsModal",
  components: {
    RefreshCwIcon,
    ImageIcon,
    RotateCcwIcon,
    InfoIcon,
    Grid3x3Icon,
    LayoutGridIcon
  },
  mixins: [mixin],
  data: () => ({
    compressQuality: [24, 25, 26, 27, 28, 29, 30],
    isReindexing: false,
    isGenerating: false,
    isRegenerating: false,
  }),
  computed: {
    ...mapGetters([
      'isCompressSettingsFetching',
      'compressSettingsData',
      'items',
      'gridDensity',
    ]),
    SETTINGS_MODAL_FIELDS_NAME() {
      return SETTINGS_MODAL_FIELDS_NAME
    },
    thumbSize() {
      return clampGridDensity(this.gridDensity);
    },
  },
  methods: {
    ...mapActions(['fetchCompressSettings', 'saveCompressSettings']),
    ...mapMutations([MUTATIONS.COMPRESS_SETTINGS.SET_DATA_KEY]),
    onThumbSizeInput(event) {
      this.$store.commit(
        `fileBrowser/${MUTATIONS.SET_GRID_DENSITY}`,
        clampGridDensity(event.target.value)
      );
    },
    filedHandler(key, value) {
      this[MUTATIONS.COMPRESS_SETTINGS.SET_DATA_KEY]({ key, value })
    },
    reindexHandler() {
      if (!this.items.length) {
        return
      }
      this.isReindexing = true
      axios.post('/file/reindex').finally(() => { this.isReindexing = false })
    },
    generateThumbnailHandler() {
      if (!this.items.length) {
        return
      }
      this.isGenerating = true
      axios.post('/file/generate_thumbnail').finally(() => { this.isGenerating = false })
    },
    reGenerateThumbnailHandler() {
      if (!this.items.length) {
        return
      }
      Swal.fire({
        title: "Are you sure you want to regenerate all thumbnails?",
        showCancelButton: true,
        confirmButtonText: "Confirm Regenerate",
        cancelButtonText: "Cancel",
        icon: "warning",
      }).then((result) => {
        if (result.value) {
          this.isRegenerating = true
          axios.post('/file/re_generate_thumbnail').finally(() => { this.isRegenerating = false })
        }
      })
    },
  }
}
</script>

<style scoped lang="scss">
::v-deep .settings-modal-dialog {
  max-width: 600px;
}

::v-deep .modal-content {
  border-radius: 10px;
}

::v-deep .modal-header {
  border-bottom: none;
  padding-bottom: 4px;
}

::v-deep .modal-body {
  padding-top: 8px;
  padding-bottom: 0;
}

::v-deep .modal-footer {
  border-top: none;
}

::v-deep .modal-title {
  font-weight: 600;
  font-size: 18px;
  font-family: system-ui;
}

.settings-loading {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: #ffffffb0;
  z-index: 999;
  display: flex;
}

.info-tooltip {
  display: inline-flex;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(107, 114, 128, 0.7);
  color: white;
  text-decoration: none;
  vertical-align: middle;
}

.general-settings-label {
  font-weight: 500;
  font-size: 14px;
}

::v-deep .nav-tabs {
  display: inline-flex;
  height: 36px;
  align-items: center;
  border-radius: 8px;
  background-color: #f1f5f9;
  padding: 4px;
  border-bottom: none;
  gap: 2px;

  .nav-item {
    margin: 0;
    padding: 0 4px;
  }

  .nav-link {
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    border: none;
    background: transparent;
    line-height: 1;
    transition: all 0.15s;

    &:hover {
      color: #374151;
      background: transparent;
      border: none;
    }

    &.active {
      background-color: white !important;
      color: #111827 !important;
      border: none !important;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }
}

::v-deep .c-settings-advanced .btn {
  border: 1px solid #e5e5e5;
  background-color: white;
  text-align: left;

  &:hover {
    background-color: #f3f4f6;
  }
}

input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  outline: none;
  cursor: pointer;
  padding: 0;
  flex: 1 1 auto;
  height: 22px;
  background: transparent;

  &::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 3px;
    background: #e5e7eb;
  }

  &.tile-size-slider::-webkit-slider-runnable-track {
    background: linear-gradient(
      to right,
      #209c86 0%,
      #209c86 var(--tile-size-pct, 0%),
      #e5e7eb var(--tile-size-pct, 0%),
      #e5e7eb 100%
    );
  }

  &::-moz-range-track {
    height: 8px;
    border-radius: 3px;
    background: #e5e7eb;
  }

  &.tile-size-slider::-moz-range-progress {
    height: 8px;
    border-radius: 3px;
    background: #209c86;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    border: 2px solid #209c86;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    margin-top: -8px;
  }

  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: white;
    border: 2px solid #209c86;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    cursor: pointer;
  }
}
</style>
